import { redirect } from "react-router";
import { useQueryClient } from "@tanstack/react-query";

import type { Route } from "./+types/$locale.catalogue.$locationSlug";
import { getLocationBySlug } from "@/api/locations";
import { buildSeoMeta } from "@/components/seo/meta";
import { Catalog } from "@/features/catalog/routes/Catalog";

/**
 * Build-time loader for /:locale/catalogue/:locationSlug.
 *
 * Resolves the slug → location via getLocationBySlug. On miss, throws a 302
 * redirect to the bare /:locale/catalogue — this replaces the runtime
 * useEffect-navigate-back hack the Catalog component used to carry, so an
 * unknown slug never reaches the component and never gets indexed.
 */
export async function loader({ params }: Route.LoaderArgs) {
    const locale = (params.locale as string) ?? "en";
    const slug = params.locationSlug as string;

    // Prerender sentinel (see react-router.config.ts): resolve without calling
    // the API, and with a 200 — the prerenderer fails the build on ANY non-2xx
    // data response (404s and redirects included). Null renders the bare catalogue.
    if (slug === "__prerender_sentinel__") {
        return { preselectedLocation: null };
    }

    const location = await getLocationBySlug(slug);
    if (!location) {
        throw redirect(`/${locale}/catalogue`);
    }
    return { preselectedLocation: location };
}

export function meta({ loaderData, params, location: routerLocation }: Route.MetaArgs) {
    const loc = loaderData?.preselectedLocation;
    const locale = (params.locale as string) ?? "en";
    const locName = loc?.name ?? "Catalogue";
    const path = routerLocation.pathname.replace(new RegExp(`^/${locale}`), "") || "/catalogue";

    return buildSeoMeta({
        title: loc
            ? `Baby Equipment Rental in ${locName} — Strollers, Cribs & Car Seats`
            : "Baby Equipment Catalogue — Rent or Buy Strollers, Cribs & More",
        description: loc
            ? `Rent premium baby equipment for pickup at ${locName}. Strollers, cribs, car seats and more — clean, safe and ready for your trip or stay.`
            : "Browse our premium baby equipment rental catalogue. Find strollers, cribs, car seats, and more for rent or purchase. Flexible rental periods and affordable pricing.",
        path,
        keywords: loc
            ? `baby equipment rental ${locName}, stroller rental ${locName}, crib rental, car seat rental, baby gear hire`
            : "baby equipment rental, stroller rental, crib rental, car seat rental, baby gear hire, baby equipment for sale",
        // TODO Phase 6: hydrate availableLocales from root loader manifest
        locale,
        availableLocales: [{ code: "en" }],
    });
}

export default function CatalogueByLocationRoute({ loaderData, params }: Route.ComponentProps) {
    const queryClient = useQueryClient();
    const slug = params.locationSlug as string;
    // `loaderData` can legitimately be `undefined` at runtime: ssr:false loaders
    // only ever run at BUILD time, for the concrete paths react-router.config.ts's
    // prerender() enumerated (see CLAUDE.md). A location created after the last
    // deploy has no static loader output — the CDN serves the SPA-fallback shell
    // and this route mounts with no loaderData at all. Reading
    // `loaderData.preselectedLocation` unguarded used to crash right here
    // (React hydration error #418); `?.` makes that case fall through to
    // <Catalog>'s client-side slug resolution below instead of throwing.
    const location = loaderData?.preselectedLocation;

    // Seed `useLocationBySlug`'s cache (key shape from app/hooks/useLocations.ts)
    // so <Catalog>'s runtime resolution — staleTime 0 + refetchOnMount 'always',
    // see app/hooks/useLocations.ts — has this build-time result to paint
    // immediately while it revalidates in the background. No-op for the
    // prerender sentinel and for slugs the build never resolved (`location` is
    // undefined/null); <Catalog> then resolves the slug client-side.
    //
    // Seed-once: this runs on every render (it's in the component body, not an
    // effect), and staleTime 0 means a background refetch can already have
    // written fresher data into this key by the time a later render fires.
    // `(old) => old ?? location` only fills the cache when it's genuinely
    // empty, so a re-render (filter change, navigate) never clobbers live data
    // with the build-time snapshot.
    if (location) {
        queryClient.setQueryData(
            ["locations", "by-slug", slug],
            (old: typeof location | undefined) => old ?? location,
        );
    }

    return <Catalog preselectedLocation={location ?? undefined} />;
}
